
import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';

export default function SuboxoneTreatment() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: 'Suboxone Treatment Thornhill | Buprenorphine Pharmacy Richmond Hill, Vaughan | Opioid Addiction Recovery',
    description: 'Professional Suboxone (buprenorphine/naloxone) treatment pharmacy in Thornhill serving Richmond Hill, Vaughan, and North York. Expert opioid addiction recovery with flexible dosing, medical monitoring, and compassionate care. Licensed Suboxone clinic with take-home options. Call (905) 709-9985 for confidential consultation.',
    keywords: 'Suboxone treatment Thornhill, Suboxone pharmacy Thornhill, buprenorphine treatment Richmond Hill, Suboxone clinic Vaughan, opioid addiction treatment Thornhill, Suboxone dosing North York, buprenorphine pharmacy near me, Suboxone take-home, opioid replacement therapy Thornhill, Suboxone clinic Richmond Hill, addiction recovery pharmacy Thornhill, buprenorphine naloxone treatment',
    canonical: `${siteUrl}/suboxone-treatment`,
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "Thornhill Drug-Mart Suboxone Treatment Pharmacy",
      "description": "Professional Suboxone (buprenorphine/naloxone) treatment pharmacy in Thornhill, Ontario providing comprehensive opioid addiction recovery services with flexible dosing options, medical monitoring, and take-home privileges. Serving Thornhill, Richmond Hill, Vaughan, North York, and Greater Toronto Area.",
      "url": `${siteUrl}/suboxone-treatment`,
      "telephone": "+1-905-709-9985",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "8108 Yonge St Unit 3-4",
        "addressLocality": "Thornhill",
        "addressRegion": "ON",
        "postalCode": "L4J 1W4",
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "43.8184",
        "longitude": "-79.4278"
      },
      "medicalSpecialty": ["Addiction Medicine", "Pharmacy"],
      "availableService": {
        "@type": "MedicalTherapy",
        "name": "Suboxone Treatment (Buprenorphine/Naloxone)",
        "description": "Evidence-based Suboxone pharmacy services for opioid addiction recovery with flexible dosing options, individualized treatment plans, comprehensive medical monitoring, counseling referrals, and take-home privileges for qualified patients"
      }
    }
  });

  const faqs = [
    {
      question: 'What is Suboxone and how does it work?',
      answer: 'Suboxone is a prescription medication containing buprenorphine and naloxone, used to treat opioid use disorder. Buprenorphine is a partial opioid agonist that reduces cravings and withdrawal symptoms without producing a strong euphoric effect. Naloxone prevents misuse by causing withdrawal if the medication is injected. Together, they provide effective opioid addiction treatment while reducing the risk of abuse.'
    },
    {
      question: 'What is the difference between Suboxone and Methadone?',
      answer: 'Suboxone (buprenorphine/naloxone) is a partial opioid agonist, while methadone is a full opioid agonist. Suboxone has a ceiling effect, meaning higher doses don\'t increase effects beyond a certain point, making it safer with lower overdose risk. Suboxone treatment often allows for more flexible dosing schedules and easier take-home privileges compared to methadone, which typically requires daily supervised dosing initially.'
    },
    {
      question: 'How is Suboxone taken?',
      answer: 'Suboxone comes in sublingual film or tablet form that dissolves under the tongue. It\'s typically taken once daily, though your doctor may prescribe a different schedule based on your needs. The medication should be placed under the tongue and allowed to dissolve completely (usually 5-10 minutes) without chewing or swallowing. Our Thornhill pharmacy team will provide detailed instructions on proper administration.'
    },
    {
      question: 'Can I take Suboxone at home?',
      answer: 'Yes, Suboxone treatment typically allows for take-home doses from the beginning or after a short observation period. Unlike methadone, which often requires daily supervised dosing initially, Suboxone\'s safety profile allows most patients to take their medication at home. You\'ll visit our Thornhill pharmacy regularly to pick up your prescription and meet with your healthcare provider for monitoring.'
    },
    {
      question: 'What are the side effects of Suboxone?',
      answer: 'Common side effects may include headache, nausea, constipation, sweating, and insomnia, especially when starting treatment. Most side effects are mild and decrease over time as your body adjusts. Serious side effects are rare when Suboxone is taken as prescribed. Our team monitors you closely and can work with your doctor to adjust your dose to minimize side effects while maintaining effectiveness.'
    },
    {
      question: 'How long does Suboxone treatment last?',
      answer: 'Suboxone treatment duration varies by individual. Some patients benefit from short-term treatment (several months to a year), while others require longer-term maintenance. Research shows that staying on medication-assisted treatment longer generally leads to better outcomes. Your healthcare team will work with you to determine the appropriate duration based on your progress, stability, and recovery goals.'
    },
    {
      question: 'Do I need a prescription for Suboxone?',
      answer: 'Yes, Suboxone requires a prescription from a physician authorized to prescribe buprenorphine for opioid use disorder. If you don\'t have a prescribing doctor, call our Thornhill pharmacy at (905) 709-9985 and we can help connect you with appropriate healthcare providers who can assess your needs and provide a prescription if appropriate.'
    },
    {
      question: 'Is Suboxone treatment covered by insurance?',
      answer: 'Many insurance plans cover Suboxone treatment, and it may also be covered under provincial health programs. Coverage varies by plan and individual circumstances. Contact our Thornhill pharmacy at (905) 709-9985 to discuss your specific insurance coverage and payment options. We\'re committed to making treatment accessible and affordable for all patients.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: 'Lower Abuse Potential',
      description: 'Partial agonist with ceiling effect reduces risk of misuse and overdose'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Take-Home Flexibility',
      description: 'Most patients can take medication at home from the start'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Reduces Cravings',
      description: 'Effectively decreases opioid cravings and withdrawal symptoms'
    },
    {
      icon: 'ri-time-line',
      title: 'Long-Acting Relief',
      description: 'Once-daily dosing provides 24-hour symptom control'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Improves Quality of Life',
      description: 'Helps patients regain stability and function normally'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Evidence-Based',
      description: 'Proven effective through extensive research and clinical use'
    }
  ];

  const treatmentProcess = [
    {
      step: '01',
      title: 'Medical Assessment',
      description: 'Comprehensive evaluation with authorized physician to determine if Suboxone is right for you',
      icon: 'ri-stethoscope-line'
    },
    {
      step: '02',
      title: 'Induction Phase',
      description: 'Carefully timed first dose when mild withdrawal symptoms begin, usually 12-24 hours after last opioid use',
      icon: 'ri-medicine-bottle-line'
    },
    {
      step: '03',
      title: 'Dose Stabilization',
      description: 'Gradual adjustment over several days to find optimal dose that controls symptoms',
      icon: 'ri-line-chart-line'
    },
    {
      step: '04',
      title: 'Maintenance Phase',
      description: 'Regular medication pickup at our Thornhill pharmacy with ongoing medical monitoring',
      icon: 'ri-calendar-check-line'
    },
    {
      step: '05',
      title: 'Counseling Support',
      description: 'Referrals to counseling and support services to address underlying issues',
      icon: 'ri-team-line'
    },
    {
      step: '06',
      title: 'Tapering (If Desired)',
      description: 'Gradual dose reduction when you and your doctor decide you\'re ready',
      icon: 'ri-arrow-down-line'
    }
  ];

  const whyChoose = [
    {
      icon: 'ri-user-smile-line',
      title: 'Experienced Team',
      description: 'Specialized pharmacists trained in addiction medicine and Suboxone treatment'
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Confidential Care',
      description: 'Private consultations and discreet service in a judgment-free environment'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Convenient Location',
      description: 'Easily accessible Thornhill location serving Richmond Hill, Vaughan, and North York'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Ongoing Support',
      description: 'Regular check-ins and support throughout your recovery journey'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20professional%20pharmacy%20interior%20with%20Suboxone%20medication%20display%2C%20clean%20medical%20environment%20with%20teal%20accents%2C%20bright%20natural%20lighting%2C%20organized%20pharmaceutical%20workspace%2C%20supportive%20addiction%20recovery%20atmosphere%2C%20compassionate%20healthcare%20setting%2C%20safe%20clinical%20consultation%20area%2C%20welcoming%20treatment%20space&width=1920&height=1000&seq=suboxone-hero-001&orientation=landscape"
          alt="Suboxone treatment pharmacy in Thornhill"
          title="Suboxone Treatment - Thornhill Drug-Mart"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

        <div className="relative z-10 w-full px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center mb-6 sm:mb-8" aria-label="Breadcrumb">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-white/80 hover:text-white transition-colors cursor-pointer"
                  aria-label="Go to homepage"
                >
                  <i className="ri-home-4-line text-base sm:text-lg"></i>
                  <span className="ml-2 text-xs sm:text-sm font-medium">Home</span>
                </button>
                <i className="ri-arrow-right-s-line text-white/60 text-base sm:text-lg"></i>
                <button
                  onClick={() => navigate('/addiction-services')}
                  className="text-white/80 hover:text-white text-xs sm:text-sm font-medium transition-colors cursor-pointer"
                >
                  Addiction Services
                </button>
                <i className="ri-arrow-right-s-line text-white/60 text-base sm:text-lg"></i>
                <span className="text-primary-400 text-xs sm:text-sm font-semibold">Suboxone Treatment</span>
              </div>
            </nav>

            {/* Badge */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="inline-flex items-center space-x-2 bg-primary-600/20 backdrop-blur-md px-3 py-1.5 rounded-full">
                <span className="text-primary-300 text-xs font-semibold uppercase tracking-wide">Buprenorphine Treatment</span>
              </div>
            </div>

            {/* Content */}
            <header className="text-center mb-8 sm:mb-10">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Suboxone Treatment (Buprenorphine/Naloxone)
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 max-w-4xl mx-auto leading-relaxed">
                Professional <strong>Suboxone pharmacy</strong> services in <strong>Thornhill</strong> for effective <strong>opioid addiction recovery</strong>. Flexible dosing, medical monitoring, and compassionate care serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>.
              </p>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
                <a
                  href="tel:9057099985"
                  className="bg-primary-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Call for Suboxone treatment consultation"
                >
                  <i className="ri-phone-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">(905) 709-9985</span>
                  <span className="sm:hidden">Call Now</span>
                </a>
                <a
                  href="/location"
                  className="bg-white text-gray-900 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Visit our Thornhill Suboxone clinic"
                >
                  <i className="ri-map-pin-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Visit Us</span>
                  <span className="sm:hidden">Location</span>
                </a>
              </div>
            </header>
          </div>
        </div>
      </section>

      {/* What is Suboxone Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                What is <strong>Suboxone Treatment</strong>?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Suboxone</strong> is a prescription medication combining <strong>buprenorphine</strong> and <strong>naloxone</strong>, used to treat <strong>opioid use disorder</strong>. As a partial opioid agonist, <strong>buprenorphine</strong> reduces cravings and withdrawal symptoms without producing a strong euphoric effect, while <strong>naloxone</strong> prevents misuse.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                Our <strong>Suboxone pharmacy</strong> in <strong>Thornhill</strong> provides comprehensive treatment services including medication dispensing, individualized treatment plans, medical monitoring, and ongoing support to help patients achieve lasting <strong>opioid addiction recovery</strong>.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Serving patients from <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, and throughout the Greater Toronto Area, our licensed <strong>Suboxone clinic</strong> offers flexible dosing options and compassionate, professional care.
              </p>
            </div>
            <div className="w-full h-96 lg:h-full rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=compassionate%20pharmacist%20consulting%20with%20patient%20about%20Suboxone%20treatment%2C%20modern%20pharmacy%20consultation%20area%20with%20teal%20accents%2C%20warm%20professional%20environment%2C%20medication%20counseling%20session%2C%20addiction%20recovery%20support%2C%20clean%20bright%20clinical%20space%2C%20empathetic%20care%20setting%2C%20supportive%20healthcare%20atmosphere&width=800&height=600&seq=suboxone-what-is-001&orientation=landscape"
                alt="Suboxone treatment consultation in Thornhill"
                title="Suboxone Consultation - Thornhill Pharmacy"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Benefits of <strong>Suboxone Treatment</strong>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              <strong>Suboxone</strong> offers unique advantages for <strong>opioid addiction recovery</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {benefits.map((benefit, index) => (
              <article 
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-primary-600 text-white rounded-lg mb-4">
                  <i className={`${benefit.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{benefit.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Process Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Your <strong>Suboxone Treatment</strong> Journey
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Step-by-step process at our <strong>Thornhill Suboxone pharmacy</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {treatmentProcess.map((step, index) => (
              <article 
                key={index}
                className="relative bg-gradient-to-br from-primary-50 to-white rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all"
              >
                <div className="absolute top-4 right-4 text-5xl sm:text-6xl font-bold text-primary-100">
                  {step.step}
                </div>
                <div className="relative">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-primary-600 text-white rounded-lg mb-4">
                    <i className={`${step.icon} text-2xl sm:text-3xl`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{step.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-primary-600 to-cyan-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              Why Choose Our <strong>Thornhill Suboxone Pharmacy</strong>?
            </h2>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto">
              Trusted <strong>Suboxone treatment</strong> provider serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((item, index) => (
              <article 
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 hover:bg-white/20 transition-all"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-white text-primary-600 rounded-lg mb-4">
                  <i className={`${item.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-white/80 leading-relaxed">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Frequently Asked <span className="text-primary-600">Questions</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Common questions about <strong>Suboxone treatment</strong> in <strong>Thornhill</strong>
            </p>
          </header>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  aria-expanded={expandedFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 
                    className="text-base sm:text-lg font-semibold text-gray-900 pr-4"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-primary-600 text-2xl flex-shrink-0`}></i>
                </button>
                {expandedFaq === index && (
                  <div 
                    id={`faq-answer-${index}`}
                    className="px-6 pb-5"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
            Start Your <strong>Suboxone Treatment</strong> Today
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-8 sm:mb-10 leading-relaxed">
            Take the first step toward <strong>opioid addiction recovery</strong> with professional <strong>Suboxone treatment</strong> at our <strong>Thornhill pharmacy</strong>. Our compassionate team provides expert care serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>. Call for a confidential consultation.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="tel:9057099985"
              className="bg-primary-600 text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Call for Suboxone treatment"
            >
              <i className="ri-phone-line mr-2"></i>
              (905) 709-9985
            </a>
            <a
              href="/addiction-services"
              className="bg-gray-900 text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-800 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="View all addiction services"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </a>
          </div>

          <div className="mt-8 sm:mt-10 flex items-center justify-center space-x-2 text-gray-600">
            <i className="ri-shield-check-line text-xl"></i>
            <p className="text-sm sm:text-base">Confidential and professional care</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
